import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BACKEND_URL } from "@/lib/api";
import { colors } from "@/theme";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function GoogleButton({ label = "Continue with Google" }) {
  return (
    <button
      type="button"
      data-testid="google-login-btn"
      onClick={startGoogleLogin}
      style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        background: colors.surface, border: `1px solid ${colors.borderStrong}`, borderRadius: 999,
        padding: "13px 26px", cursor: "pointer", fontFamily: "DM Sans", fontWeight: 600, fontSize: 14, color: colors.onSurface,
        transition: "background-color 0.2s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = colors.surfaceSecondary)}
      onMouseLeave={(e) => (e.currentTarget.style.background = colors.surface)}
    >
      <GoogleIcon /> {label}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

// Detects the OAuth return (#session_id=...), exchanges it for our JWT, then routes on.
export function GoogleAuthHandler() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m) return;
    ran.current = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/google/session`, {
          method: "POST",
          headers: { "X-Session-ID": decodeURIComponent(m[1]) },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Google sign-in failed");
        loginWithToken(data.access_token, data.user);
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${(data.user.name || "").split(" ")[0]}`);
        navigate(data.user.role === "admin" ? "/admin" : "/", { replace: true });
      } catch (e) {
        window.history.replaceState(null, "", window.location.pathname);
        toast.error(e.message);
        navigate("/login", { replace: true });
      }
    })();
  }, [loginWithToken, navigate]);

  return null;
}
